import { Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IActivityLog,
  ICameraGroupChangeRecord,
  IEventTriggerInfo,
  CameraGroupChangeRecordField,
  VISIBLE_DETAILS_FIELDS_COUNT
} from 'app/models/activity-log.model';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { IdentityFormatPipe, IncludesPipe, JoinPipe, MomentPipe, SomePipe, WeekdayPipe } from 'app/pipes/pipes';
import { FormatCameraDetailsPipe } from './activity-camera-group-details-pipes';
import { ApiService } from 'app/services/api.service';

type IActivationTranslation = {
  group: number;
  node: number;
  deviceType: string;
  name: string;
};

@Component({
    selector: 'app-activity-camera-group-details',
    imports: [
        CommonModule, TranslateModule, RouterModule, IdentityFormatPipe, MomentPipe,
        IncludesPipe, SomePipe, JoinPipe, FormatCameraDetailsPipe, WeekdayPipe
    ],
    templateUrl: './activity-camera-group-details.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    styleUrls: ['../activity-details-dialog/activity-details-content-styles.css']
})
export class ActivityCameraGroupDetailsComponent implements OnChanges {
  @Output() closeDialog = new EventEmitter<void>();
  @Input() log: IActivityLog;

  readonly visibleFieldCount = VISIBLE_DETAILS_FIELDS_COUNT;

  deviceNameMap: Record<string, string> = {};
  activationNameMap: Record<string, string> = {};
  cameraGroupChanges: ICameraGroupChangeRecord;
  visibleFields: CameraGroupChangeRecordField[] = [];
  allFields: CameraGroupChangeRecordField[] = [];
  language: string = localStorage.getItem('language');
  isNightowl: boolean = false;

  constructor(private apiService: ApiService) {}

  getDeviceName(id: string): string {
    if (!id) return 'NONE';
    return this.deviceNameMap[id] ? `${this.deviceNameMap[id]} (${id})` : id;
  }

  ngOnChanges(): void {
    // Copy the group changes to a local variable to just make the template little more cleaner.
    this.cameraGroupChanges = this.log?.details?.cameraGroupChanges;
    const result = this.getVisibleFields(this.cameraGroupChanges);
    this.visibleFields = [...result.visibleFields]; // Change the visible fields to a new array to trigger change detection.
    this.allFields = [...result.allFields];

    this.apiService.getDevices({}).subscribe({
      next: devices => {
        this.deviceNameMap = {};
        (devices ?? []).forEach(d => this.deviceNameMap[d.id] = d.name);
      },
      error: () => { /* Devices are shown by ID */ }
    });

    this.initializeActivationNameMap();
  }

  private initializeActivationNameMap(): void {
    const cached: IActivationTranslation[] = JSON.parse(localStorage.getItem('act') || '[]');
    this.populateActivationNameMap(cached);

    // Keep names aligned with the current UI language.
    this.apiService.getActivationTranslations(this.language).subscribe((translations: IActivationTranslation[]) => {
      this.populateActivationNameMap(translations);
    });
  }

  private populateActivationNameMap(translations: IActivationTranslation[]): void {
    if (!translations?.length) {
      return;
    }

    this.activationNameMap = {};
    translations.forEach((item) => {
      const key = this.getEventTriggerKey(item.deviceType, String(item.group), String(item.node));
      this.activationNameMap[key] = item.name;
    });
  }

  private getEventTriggerKey(devicePrefixClass: string, alarmGroup: string, alarmNode: string): string {
    return `${devicePrefixClass}|${alarmGroup}|${alarmNode}`;
  }

  private getVisibleFields(cameraGroupChanges: ICameraGroupChangeRecord): { visibleFields: CameraGroupChangeRecordField[], allFields: CameraGroupChangeRecordField[] } {
    // Return empty array if the object is falsy.
    if (!cameraGroupChanges) {
      return { visibleFields: [], allFields: [] };
    }

    const visibleFields: CameraGroupChangeRecordField[] = [];
    const allFields = Object.keys(cameraGroupChanges).filter(obj => obj) as CameraGroupChangeRecordField[]; // Get the all fields that are not falsy.

    // Camera changes have higher priority, so they are always visible if they exists.
    if (allFields.includes("addedCameras")) {
      visibleFields.push("addedCameras");
    }

    if (allFields.includes("modifiedCameras")) {
      visibleFields.push("modifiedCameras");
    }

    if (allFields.includes("removedCameras")) {
      visibleFields.push("removedCameras");
    }

    for (const field of allFields) {
      this.addVisibleField(field, allFields, visibleFields);
    }

    return { visibleFields, allFields };
  }

  private addVisibleField(field: CameraGroupChangeRecordField, allFields: CameraGroupChangeRecordField[], visibleFields: CameraGroupChangeRecordField[]): void {
    // Do nothing if the field is already visible or the limit is reached.
    if (this.isFieldAddable(field, visibleFields) === false) {
      return;
    }

    switch (field) {
      case "addedCameras":
      case "modifiedCameras":
      case "removedCameras":
        const cameraChanges = this.filterFields(allFields, "addedCameras", "modifiedCameras", "removedCameras");
        this.addIfSpace(visibleFields, cameraChanges);
        break;
      case "addedAlertDevices":
      case "removedAlertDevices":
        const receiverTeamsChanges = this.filterFields(allFields, "addedAlertDevices", "removedAlertDevices");
        this.addIfSpace(visibleFields, receiverTeamsChanges);
        break;
      case "addedEventTriggers":
      case "removedEventTriggers":
        const eventTriggerChanges = this.filterFields(allFields, "addedEventTriggers", "removedEventTriggers");
        this.addIfSpace(visibleFields, eventTriggerChanges);
        break;
      case "addedReceiverGroups":
      case "removedReceiverGroups":
        const alarmRoutesChanges = this.filterFields(allFields, "addedReceiverGroups", "removedReceiverGroups");
        this.addIfSpace(visibleFields, alarmRoutesChanges);
        break;
      case "removedVisits":
        const removedVisits = this.filterFields(allFields, "removedVisits");
        this.addIfSpace(visibleFields, removedVisits);
        break;
      default:
        visibleFields.push(field);
        break;
    }
  }

  private isFieldAddable(field: CameraGroupChangeRecordField, visibleFields: CameraGroupChangeRecordField[]): boolean {
    if (visibleFields.length >= this.visibleFieldCount || visibleFields.includes(field)) {
      return false;
    }
    return true;
  }

  private filterFields(allFields: CameraGroupChangeRecordField[], ...fields: CameraGroupChangeRecordField[]): CameraGroupChangeRecordField[] {
    return allFields.filter(f => {
      return fields.some(field => f === field);
    })
  }

  private addIfSpace(visibleFields: CameraGroupChangeRecordField[], fieldsToAdd: CameraGroupChangeRecordField[]): void {
    // Add the fields if there is enough space.
    if (visibleFields.length >= this.visibleFieldCount - fieldsToAdd.length) {
      return;
    }
    fieldsToAdd.forEach(f => visibleFields.push(f));
  }

    // Helper to get device type name from localStorage
    getDeviceTypeName(deviceType: string): string {
      const devTypes = JSON.parse(localStorage.getItem('devType') || '[]');
      const found = devTypes.find((d: any) => d.deviceType === deviceType);
      return found ? found.deviceTypeName : deviceType;
    }

    // Group triggers by deviceType
    groupEventTriggersByDeviceType(triggers?: IEventTriggerInfo[]): { deviceType: string, triggers: IEventTriggerInfo[] }[] {
      if (!triggers || triggers.length === 0) return [];
      const groups: { [deviceType: string]: IEventTriggerInfo[] } = {};
      triggers.forEach(trigger => {
        const type = trigger.devicePrefixClass;
        if (!groups[type]) groups[type] = [];
        groups[type].push(trigger);
      });
      return Object.keys(groups).map(deviceType => ({ deviceType, triggers: groups[deviceType] }));
    }

  formatEventTriggers(triggers?: IEventTriggerInfo[]): string {
    if (!triggers || triggers.length === 0) {
      return "NONE";
    }

    return triggers
      .map((trigger) => {
        const key = this.getEventTriggerKey(trigger.devicePrefixClass, trigger.alarmGroup, trigger.alarmNode);
        const alarmTypeName = this.activationNameMap[key];
        if (alarmTypeName) {
          return `${alarmTypeName}`;
        }
        return `${trigger.devicePrefixClass}:${trigger.alarmGroup}:${trigger.alarmNode}`;
      })
      .join(', ');
  }
}
